import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  OpenInNew as DetailIcon,
  FlashOn as QuickReceiveIcon,
  Dashboard as DashboardIcon,
  Tune as AdjustIcon,
  NotificationsNone as AlertsIcon,
  Upload as ImportIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import venueLocationService from '../../../../services/venueLocationService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import QuickReceiveModal from '../../../../components/venue/QuickReceiveModal';
import InventoryImportDialog from '../../../../components/venue/InventoryImportDialog';
import type { InventoryItemWithBalance, StockStatus } from '../../../../services/inventoryItemService';
import { relativeDate } from '../../../../utils/dateUtils';
import type { VenueLocation } from '../../../../types/venue';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const COL_COUNT = 8; // Item (w/ size), Qty, Status, Par, Last Cost, Last Supplier, Source, actions

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive stock status from local data.
 * Used for display while the backend Phase 10 status field is not yet available,
 * and as a fallback if the backend omits it.
 */
function deriveStatus(item: InventoryItemWithBalance): StockStatus | null {
  // Prefer backend-derived value once available
  if (item.stockStatus !== undefined) return item.stockStatus ?? null;

  const qty = item.balance?.totalQuantity;
  if (qty == null) return null;
  if (qty <= 0) return 'OUT';
  if (item.reorderPoint != null && qty <= item.reorderPoint) return 'LOW';
  if (item.reorderPoint != null) return 'OK';
  return null; // no reorderPoint configured
}

/** Formats supplier for a compact list cell. Shows last received supplier + date if available,
 *  falls back to preferred supplier link name, then dash. */
function fmtSupplier(
  lastSupplierName: string | undefined,
  date: string | undefined,
  preferredSupplierName: string | null | undefined,
): string {
  const name = lastSupplierName || preferredSupplierName;
  if (name && date) return `${name} · ${relativeDate(date)}`;
  if (name) return name;
  if (date) return relativeDate(date);
  return '—';
}

// ── Stock status badge ────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<StockStatus, { label: string; color: string; bg: string }> = {
  OUT: { label: 'Out',  color: '#cf222e', bg: '#ffebe9' },
  LOW: { label: 'Low',  color: '#9a6700', bg: '#fef9c3' },
  OK:  { label: 'OK',   color: '#1a7f37', bg: '#dafbe1' },
};

function StockStatusBadge({ status }: { status: StockStatus | null }) {
  if (!status) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }
  const cfg = STATUS_CONFIG[status];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: 'none',
        height: 22,
        minWidth: 36,
      }}
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryItemList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { venueId, defaultCurrency } = useVenue();

  // ── Filters — initialise from URL query params so dashboard cards can link in ─
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockStatus | ''>(() => {
    const s = searchParams.get('status');
    return (s === 'LOW' || s === 'OUT' || s === 'OK') ? s : '';
  });
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('active');

  // ── Quick Receive ─────────────────────────────────────────────────────────────
  const [quickReceiveOpen, setQuickReceiveOpen] = useState(false);

  // ── Import ────────────────────────────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<InventoryItemWithBalance[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0); // MUI is 0-based; API is 1-based
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [locations, setLocations] = useState<VenueLocation[]>([]);

  // ── Load locations once ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Debounced search ──────────────────────────────────────────────────────────
  const applySearch = useMemo(
    () => debounce((q: string) => { setSearchQuery(q); setPage(0); }, 350),
    [],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    applySearch(e.target.value);
  };

  // ── Load items ────────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryItemService.getItems(
        venueId,
        page + 1,
        pageSize,
        'displayName',
        'asc',
        searchQuery || undefined,
        locationFilter || undefined,
        statusFilter || undefined,
        activeFilter,
      );
      setItems(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, searchQuery, locationFilter, statusFilter, activeFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const itemBase = (itemId: string) => `/admin/venues/${venueId}/inventory/${itemId}`;


  // ── Empty state message ───────────────────────────────────────────────────────
  const emptyMessage = (): string => {
    if (statusFilter === 'LOW') {
      return 'No low-stock items found. Set reorder points on inventory items to enable low-stock alerts.';
    }
    if (statusFilter === 'OUT') {
      return 'No out-of-stock items. All tracked items have stock.';
    }
    if (activeFilter === 'inactive') {
      return 'No inactive items found.';
    }
    if (searchQuery || locationFilter || statusFilter) {
      return 'No items match the current filters.';
    }
    return 'No inventory items yet. Use Add Item to start tracking stock.';
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Inventory Items</Typography>
          {!loading && totalItems > 0 && (
            <Typography variant="caption" color="text.secondary">({totalItems})</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/dashboard`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<AlertsIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/alerts`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Alerts
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<QuickReceiveIcon />}
            onClick={() => setQuickReceiveOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Quick Receive
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/create`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search items"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ minWidth: 260, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={locationFilter}
            label="Location"
            onChange={e => { setLocationFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Stock status</InputLabel>
          <Select
            value={statusFilter}
            label="Stock status"
            onChange={e => { setStatusFilter(e.target.value as StockStatus | ''); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="LOW">Low stock</MenuItem>
            <MenuItem value="OUT">Out of stock</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Items</InputLabel>
          <Select
            value={activeFilter}
            label="Items"
            onChange={e => { setActiveFilter(e.target.value as 'active' | 'inactive' | 'all'); setPage(0); }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="all">All items</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Error */}
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer sx={{ overflowX: 'auto', px: 1.5, pt: 0.5, pb: 1.5 }}>
          <Table sx={{ tableLayout: 'fixed', minWidth: 990, borderCollapse: 'separate', borderSpacing: '0 5px' }}>
            <colgroup>
              <col style={{ width: 310 }} />  {/* Item */}
              <col style={{ width: 104 }} />  {/* Total Stock */}
              <col style={{ width: 70 }}  />  {/* Status */}
              <col style={{ width: 52 }}  />  {/* Par */}
              <col style={{ width: 96 }}  />  {/* Last cost */}
              <col style={{ width: 130 }} />  {/* Supplier */}
              <col style={{ width: 100 }} />  {/* Source */}
              <col style={{ width: 88 }}  />  {/* Actions */}
            </colgroup>
            <TableHead>
              <TableRow>
                {([
                  'Item', 'Total Stock', 'Status', 'Par',
                  'Last cost', 'Supplier', 'Source', '',
                ] as const).map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 500,
                      color: alpha(theme.palette.text.primary, 0.38),
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      whiteSpace: 'nowrap',
                      py: 1.25,
                      border: 'none',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <InventoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.25 }} />
                      <Typography variant="body2">{emptyMessage()}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => {
                  const status = deriveStatus(item);
                  return (
                    <TableRow
                      key={item._id}
                      sx={{
                        cursor: 'pointer',
                        '& td': {
                          backgroundColor: theme.palette.background.paper,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          transition: 'background-color 0.15s',
                        },
                        '& td:first-of-type': {
                          borderLeft: `1px solid ${theme.palette.divider}`,
                          borderRadius: '8px 0 0 8px',
                        },
                        '& td:last-of-type': {
                          borderRight: `1px solid ${theme.palette.divider}`,
                          borderRadius: '0 8px 8px 0',
                        },
                        '&:hover td': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                      onClick={() => navigate(itemBase(item._id))}
                    >
                      {/* Item — primary identity, 2-line clamp */}
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography
                          fontSize="0.83rem"
                          fontWeight={500}
                          sx={{
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {item.displayName}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={item.type === 'WINE' ? 'Wine' : 'Generic'}
                            size="small"
                            sx={{
                              fontSize: '0.63rem',
                              height: 18,
                              fontWeight: 600,
                              color: item.type === 'WINE' ? '#6941c6' : theme.palette.text.secondary,
                              backgroundColor: item.type === 'WINE'
                                ? alpha('#6941c6', 0.08)
                                : alpha(theme.palette.text.secondary, 0.08),
                              border: 'none',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {item.balance != null ? (
                          <InventoryQuantityBadge
                            quantity={item.balance.totalQuantity}
                            parLevel={item.parLevel}
                            reorderPoint={item.reorderPoint}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <StockStatusBadge status={status} />
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize="0.78rem" color="text.secondary">
                          {item.parLevel != null ? item.parLevel : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {item.balance?.lastCost != null ? (
                          <Box>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'text.disabled', lineHeight: 1.2 }}>
                              {defaultCurrency ?? '\u00a0'}
                            </Typography>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.2 }}>
                              {item.balance.lastCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize="0.78rem" color="text.secondary" noWrap>
                          {fmtSupplier(item.lastSupplierName, item.lastReceivedDate, item.preferredSupplierName)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Chip
                          label={item.source}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.68rem', height: 20, opacity: 0.7, maxWidth: '100%' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <Tooltip title="Adjust stock">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/venues/${venueId}/inventory/adjust?itemId=${item._id}`)}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <AdjustIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View detail">
                          <IconButton
                            size="small"
                            onClick={() => navigate(itemBase(item._id))}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <DetailIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalItems > pageSize && (
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        )}
      </Paper>

      <QuickReceiveModal
        open={quickReceiveOpen}
        onClose={() => setQuickReceiveOpen(false)}
        onSuccess={loadItems}
        venueId={venueId}
      />

      <InventoryImportDialog
        open={importOpen}
        venueId={venueId ?? ''}
        onClose={() => setImportOpen(false)}
        onSuccess={loadItems}
      />
    </Box>
  );
};

export default InventoryItemList;
